import React, { useCallback, useState } from 'react';
import { combineClasses } from '../../../utils/formatters';
import { TOPBAR_CONFIG } from '../../../config/topbarConfig';
import { FORM_CONFIG } from '../../../config';
import authService from '../../../services/authService';
import { CartIcon, UserIcon } from '../../ui/SvgIcons';
import TopBarAuthModal from './TopBarAuthModal';

// Cart Button Component - Memoized for performance
export const TopBarCart = React.memo(({ 
  showCartIcon, 
  onCartClick, 
  cartCount, 
  formatCartCount 
}) => {
  const handleCartClick = useCallback(() => {
    if (onCartClick) {
      onCartClick();
    }
  }, [onCartClick]);

  if (!showCartIcon) return null;

  return (
    <button 
      onClick={handleCartClick}
      className={combineClasses(
        TOPBAR_CONFIG.COMPONENTS.button.base,
        TOPBAR_CONFIG.COMPONENTS.display.relative,
        `text-${TOPBAR_CONFIG.COLORS.secondary}`,
        TOPBAR_CONFIG.HOVER_EFFECTS.button,
        TOPBAR_CONFIG.TRANSITIONS.default
      )}
      aria-label={`Booking cart with ${cartCount} items`}
      aria-describedby={cartCount > 0 ? "cart-badge" : undefined}
    >
      <CartIcon className={TOPBAR_CONFIG.ICONS.cart.size} />
      {cartCount > 0 && (
        <span 
          id="cart-badge"
          className={combineClasses(
            TOPBAR_CONFIG.CART.badgePosition,
            TOPBAR_CONFIG.CART.badgeSize,
            TOPBAR_CONFIG.CART.badgeColors,
            TOPBAR_CONFIG.CART.badgeBorder,
            TOPBAR_CONFIG.COMPONENTS.misc.roundedFull,
            TOPBAR_CONFIG.COMPONENTS.display.flex,
            TOPBAR_CONFIG.COMPONENTS.alignment.itemsCenter,
            TOPBAR_CONFIG.COMPONENTS.alignment.justifyCenter,
            TOPBAR_CONFIG.COMPONENTS.typography.fontMedium,
            TOPBAR_CONFIG.COMPONENTS.spacing.paddingX1,
            TOPBAR_CONFIG.COMPONENTS.misc.zIndex10
          )}
          aria-label={`${cartCount} items in cart`}
        >
          {formatCartCount(cartCount)}
        </span>
      )}
    </button>
  );
});

TopBarCart.displayName = 'TopBarCart';

// User Menu Items Component - Optimized
export const UserMenuItems = React.memo(({ 
  userMenuItems, 
  handleUserMenuClick 
}) => {
  const handleItemClick = useCallback((action) => {
    handleUserMenuClick(action);
  }, [handleUserMenuClick]);

  return (
    <>
      {userMenuItems.map((item) => (
        <button
          key={item.id}
          onClick={() => handleItemClick(item.action)}
          className={combineClasses(
            TOPBAR_CONFIG.COMPONENTS.dropdown.menuItem,
            `text-${TOPBAR_CONFIG.COLORS.text}`,
            TOPBAR_CONFIG.HOVER_EFFECTS.dropdown,
            TOPBAR_CONFIG.TRANSITIONS.default
          )}
          role="menuitem"
          tabIndex={-1}
        >
          {item.icon && (
            <span 
              className={combineClasses(
                TOPBAR_CONFIG.COMPONENTS.spacing.marginRight3, 
                TOPBAR_CONFIG.COMPONENTS.typography.textBase
              )}
              aria-hidden="true"
            >
              {item.icon}
            </span>
          )}
          {item.label}
        </button>
      ))}
    </>
  );
});

UserMenuItems.displayName = 'UserMenuItems';

// Signup Options Component - Optimized
export const SignupOptions = React.memo(({ 
  signupOptions, 
  handleUserMenuClick 
}) => {
  const handleSignupClick = useCallback((action) => {
    handleUserMenuClick(action);
  }, [handleUserMenuClick]);

  return (
    <>
      <div 
        className={combineClasses(
          `border-t border-${TOPBAR_CONFIG.COLORS.borderLight}`, 
          TOPBAR_CONFIG.COMPONENTS.dropdown.sectionDivider
        )}
        role="separator"
        aria-hidden="true"
      ></div>
      <div className={TOPBAR_CONFIG.COMPONENTS.dropdown.signupContainer}>
        <p className={TOPBAR_CONFIG.USER_MENU.signupSection.titleStyle}>
          {TOPBAR_CONFIG.USER_MENU.signupSection.title}
        </p>
        {signupOptions.map((option) => (
          <button
            key={option.id}
            onClick={() => handleSignupClick(option.action)}
            className={combineClasses(
              TOPBAR_CONFIG.COMPONENTS.dropdown.signupItem,
              `text-${TOPBAR_CONFIG.COLORS.text}`,
              TOPBAR_CONFIG.HOVER_EFFECTS.dropdown,
              TOPBAR_CONFIG.TRANSITIONS.default
            )}
            role="menuitem"
            tabIndex={-1}
          >
            <div className={TOPBAR_CONFIG.COMPONENTS.typography.fontMedium}>
              {option.label}
            </div>
            <div className={combineClasses(
              TOPBAR_CONFIG.COMPONENTS.typography.textXs, 
              `text-${TOPBAR_CONFIG.COLORS.textLight}`
            )}>
              {option.description}
            </div>
          </button>
        ))}
      </div>
    </>
  );
});

SignupOptions.displayName = 'SignupOptions';

// User Info Section Component - Optimized
export const UserInfoSection = React.memo(({ isLoggedIn, userInfo }) => (
  <div className={combineClasses(
    TOPBAR_CONFIG.COMPONENTS.dropdown.userInfo, 
    `border-b border-${TOPBAR_CONFIG.COLORS.borderLight}`
  )}>
    {isLoggedIn && userInfo ? (
      <>
        <p className={combineClasses(
          TOPBAR_CONFIG.COMPONENTS.typography.textSm, 
          TOPBAR_CONFIG.COMPONENTS.typography.fontMedium, 
          `text-${TOPBAR_CONFIG.COLORS.textDark}`
        )}>
          {TOPBAR_CONFIG.USER_MENU.welcomeMessage.loggedIn.title(userInfo.name)}
        </p>
        <p className={combineClasses(
          TOPBAR_CONFIG.COMPONENTS.typography.textXs, 
          `text-${TOPBAR_CONFIG.COLORS.textLight}`
        )}>
          {userInfo.email}
        </p>
      </>
    ) : (
      <>
        <p className={combineClasses(
          TOPBAR_CONFIG.COMPONENTS.typography.textSm, 
          TOPBAR_CONFIG.COMPONENTS.typography.fontMedium, 
          `text-${TOPBAR_CONFIG.COLORS.textDark}`
        )}>
          {TOPBAR_CONFIG.USER_MENU.welcomeMessage.guest.title}
        </p>
        <p className={combineClasses(
          TOPBAR_CONFIG.COMPONENTS.typography.textXs, 
          `text-${TOPBAR_CONFIG.COLORS.textLight}`
        )}>
          {TOPBAR_CONFIG.USER_MENU.welcomeMessage.guest.subtitle}
        </p>
      </>
    )}
  </div>
));

UserInfoSection.displayName = 'UserInfoSection';

// User Dropdown Component - Optimized with accessibility
export const UserDropdown = React.memo(({ 
  showUserDropdown, 
  isLoggedIn, 
  userInfo, 
  userMenuItems, 
  handleUserMenuClick, 
  signupOptions 
}) => {
  if (!showUserDropdown) return null;

  return (
    <div 
      className={combineClasses(
        TOPBAR_CONFIG.COMPONENTS.dropdown.userContainer,
        TOPBAR_CONFIG.USER_MENU.dropdownWidth,
        `border border-${TOPBAR_CONFIG.COLORS.border}`
      )}
      role="menu"
      aria-label="User menu"
    >
      <UserInfoSection isLoggedIn={isLoggedIn} userInfo={userInfo} />
      <UserMenuItems userMenuItems={userMenuItems} handleUserMenuClick={handleUserMenuClick} />
      {!isLoggedIn && signupOptions && signupOptions.length > 0 && (
        <SignupOptions signupOptions={signupOptions} handleUserMenuClick={handleUserMenuClick} />
      )}
    </div>
  );
});

UserDropdown.displayName = 'UserDropdown';

// User Menu Component - Optimized with keyboard navigation and authentication modal
export const TopBarUserMenu = React.memo(({ 
  showUserIcon,
  showUserDropdown,
  setShowUserDropdown,
  isLoggedIn,
  userInfo,
  userMenuItems,
  handleUserMenuClick,
  signupOptions,
  userDropdownRef,
  onLogin,
  onSignup
}) => {
  const [authModal, setAuthModal] = useState({ isOpen: false, mode: 'login' });
  const [selectedUserType, setSelectedUserType] = useState(FORM_CONFIG.USER_TYPES.USER);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleToggleDropdown = useCallback(() => {
    setShowUserDropdown(prev => !prev);
  }, [setShowUserDropdown]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggleDropdown();
    } else if (event.key === 'Escape') {
      setShowUserDropdown(false);
    }
  }, [handleToggleDropdown, setShowUserDropdown]);

  const handleAuthAction = useCallback((action) => {
    // Close dropdown
    setShowUserDropdown(false);
    
    // Open appropriate auth modal
    if (action === 'login') {
      setAuthModal({ isOpen: true, mode: 'login' });
    } else if (action === 'signup') {
      setAuthModal({ isOpen: true, mode: 'signup' });
    } else {
      // Handle other menu actions
      if (handleUserMenuClick) {
        handleUserMenuClick(action);
      }
    }
  }, [setShowUserDropdown, handleUserMenuClick]);

  const handleCloseAuthModal = useCallback(() => {
    setAuthModal({ isOpen: false, mode: 'login' });
    setError('');
    setLoading(false);
  }, []);

  const handleSignup = useCallback(async (formData) => {
    setLoading(true);
    setError('');

    try {
      const signupData = {
        fullName: formData.get('fullName'),
        email: formData.get('email'),
        phone: formData.get('phone') || '',
        password: formData.get('password'),
        userType: selectedUserType
      };

      console.log('Submitting signup data:', { ...signupData, password: '***hidden***' });

      if (onSignup) {
        await onSignup(signupData);
      } else {
        await authService.signup(signupData);
      }

      alert('Registration successful! Welcome to PoojaBooking.');
      handleCloseAuthModal();

    } catch (error) {
      console.error('Signup error:', error);
      setError(error.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedUserType, onSignup, handleCloseAuthModal]);

  const handleLogin = useCallback(async (formData) => {
    setLoading(true);
    setError('');

    try {
      const loginData = {
        email: formData.get('email'),
        password: formData.get('password'),
        rememberMe: Boolean(formData.get('rememberMe'))
      };

      console.log('Attempting login for:', loginData.email);

      if (onLogin) {
        await onLogin(loginData);
      } else {
        await authService.login(loginData);
      }

      handleCloseAuthModal();

      alert('Welcome back to PoojaBooking!');

    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [onLogin, handleCloseAuthModal]);

  if (!showUserIcon) return null;

  return (
    <>
      <div className={TOPBAR_CONFIG.COMPONENTS.display.relative} ref={userDropdownRef}>
        <button 
          onClick={handleToggleDropdown}
          onKeyDown={handleKeyDown}
          className={combineClasses(
            TOPBAR_CONFIG.COMPONENTS.button.base,
            `text-${TOPBAR_CONFIG.COLORS.secondary}`,
            TOPBAR_CONFIG.HOVER_EFFECTS.button,
            TOPBAR_CONFIG.TRANSITIONS.default
          )}
          aria-label="User menu"
          aria-expanded={showUserDropdown}
          aria-haspopup="menu"
        >
          <UserIcon />
        </button>

        <UserDropdown
          showUserDropdown={showUserDropdown}
          isLoggedIn={isLoggedIn}
          userInfo={userInfo}
          userMenuItems={userMenuItems}
          handleUserMenuClick={handleAuthAction}
          signupOptions={signupOptions}
        />
      </div>

      <TopBarAuthModal
        isOpen={authModal.isOpen}
        mode={authModal.mode}
        error={error}
        loading={loading}
        selectedUserType={selectedUserType}
        onUserTypeChange={setSelectedUserType}
        onClose={handleCloseAuthModal}
        onSwitchMode={(mode) => setAuthModal((prev) => ({ ...prev, mode }))}
        onLoginSubmit={handleLogin}
        onSignupSubmit={handleSignup}
      />
    </>
  );
});

TopBarUserMenu.displayName = 'TopBarUserMenu';
